import Axios from 'axios';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import * as _ from 'lodash';
import { Link, useLocation } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { CardView, Grid, Lock } from '../../assets/icons';
import { PractitionerContext } from '../../contexts/Practitioner';
import { PatientsContext } from '../../contexts/PractitionerPatientsContext';
import { auth } from '../../firebase/firebase';
import { ViviPatient } from '../../interfaces/patient';
import { getFullPatientFromState } from '../../machines/patient.machine';
import { PractitionerPatientsMachineStates } from '../../machines/patients.machine';
import { clearAlert } from '../../services/api/insight';
import { firebaseSendCurrentScreen } from '../../utils/analytics';
import { HomeActions, PATIENT_STATUS } from '../../utils/constants';
import { getPatientStatus } from '../../utils/data-parser';
import { useDeviceDetect } from '../../utils/hooks';
import { notAsked, RemoteData } from '../../utils/remote-data';
import UpsertClientContainer from '../admin-clients/upsert-client-container';
import InsightDetailsModal from '../insights/insight-details-modal';
import styles from '../insights/insights.module.scss';
import CardsView from './cards-view';
import './home.scss';
import TableView from './table-view';
import SearchInput from '../common/table/search-input';
import { NoteModal } from '../note-modal/note-modal';
import { NotesResult } from '../../interfaces/note-result';
import ChatIcon from '@mui/icons-material/Chat';

export const Home = ({ history }): JSX.Element => {
  const query = new URLSearchParams(useLocation().search);
  const { isMobile } = useDeviceDetect();
  const { addToast } = useToasts();
  const { practitioner, practitionerViewConfig, setPractitionerViewConfig } = useContext(PractitionerContext);
  const [{ selectedPatient, selectedAction }, setSelectedPatientAction] = useState<{
    selectedPatient: ViviPatient | undefined;
    selectedAction: HomeActions | string;
  }>({
    selectedPatient: undefined,
    selectedAction: '',
  });
  const [lastNote, setLastNote] = useState<RemoteData<NotesResult>>(notAsked());

  const [filterValue, setFilterValue] = useState('');
  const [showUpsertClientModal, setShowUpsertClientModal] = useState<boolean>(false);
  const [criticalCollapsed, setCriticalCollapsed] = useState<boolean>(false);
  const [atRiskCollapsed, setAtRiskCollapsed] = useState<boolean>(false);
  const [stableCollapsed, setStableCollapsed] = useState<boolean>(false);
  const [showDischargeClientModal, setShowDischargeClientModal] = useState<boolean>(false);
  const { loadPractitionerPatients, practitionerPatientsRef, practitionerPatientsState, changeInsight } =
    useContext(PatientsContext);

  const patientsRef = practitionerPatientsRef;
  const isLoadingPatients =
    practitionerPatientsState === PractitionerPatientsMachineStates.Loading ||
    practitionerPatientsState === PractitionerPatientsMachineStates.Idle;
  const patients = _.uniqBy((patientsRef || []).map(getFullPatientFromState), 'id');

  useEffect(() => {
    if (practitioner) {
      loadPractitionerPatients(practitioner?.id, 'triageView');
    }
  }, [practitioner]);

  useEffect(() => {
    if (practitioner) {
      console.log('Firebase ID:', practitioner?.firebaseUid);
      firebaseSendCurrentScreen('TriagePage', {
        practitionerId: practitioner?.id,
        organizationId: practitioner?.organization.id,
        firebaseUid: practitioner?.firebaseUid,
      });
    }
  }, [practitioner]);

  const updatePersonalView = async (): Promise<void> => {
    const triageView = query.get('view')?.toUpperCase();
    const token = await auth.currentUser?.getIdToken();
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };
    const res = await Axios.patch(
      `${process.env.REACT_APP_WEB_API_URL}/practitioners/me/view-settings`,
      { triageView },
      config,
    ).catch(err => console.log(err));

    if (!res) {
      return;
    }
    if (triageView) {
      setPractitionerViewConfig(triageView);
    }
  };

  const filter = (e: any): void => {
    const val = e.target.value || '';
    setFilterValue(val);
  };

  const getPatientStatusElement = (score: number): JSX.Element => {
    const status = getPatientStatus(score);
    if (status === PATIENT_STATUS.AT_RISK) {
      return <span className={styles.statusAtRisk}>AT RISK</span>;
    }
    if (status === PATIENT_STATUS.CRITICAL) {
      return <span className={styles.statusCritical}>CRITICAL</span>;
    }
    return <span className={styles.statusStable}>STABLE</span>;
  };

  const clearAlertAction = async (patientId: string, insightId: number) => {
    setSelectedPatientAction({ selectedPatient: undefined, selectedAction: '' });
    const res = await clearAlert(patientId, insightId);

    if (res.status === 'Done') {
      const updatedPatientInsight = patients.find(patient => patient.id === patientId)!.insight;
      const insight = {
        ...updatedPatientInsight,
        clearedAt: moment.utc().format(),
        clearedBy: practitioner?.id,
      };
      changeInsight(patientId, insight);
      addToast('Alert cleared', {
        appearance: 'success',
        autoDismiss: true,
      });
    } else {
      addToast(`Unable to clear Alert - Error: ${res.errorApi.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };

  useEffect(() => {
    // only run if query param is empty
    if (query.get('view')) {
      return;
    }
    const path = practitioner?.viewSettings?.triageView;
    switch (path) {
      case 'CARD':
        history.push('/home?view=card');
        break;
      case 'TABLE':
        history.push('/home?view=table');
        break;
    }
  }, [practitioner, history, query]);

  return (
    <div className={`container ${query.get('view') === 'card' && !isMobile ? 'full-view' : ''}`}>
      {/* Top ROW */}
      <div className='menuRow'>
        <div className='left'>
          <SearchInput
            searchValue={filterValue}
            handleInputChange={filter}
            handleOnClick={() => setFilterValue('')}
            placeholder={'Search for Client Name'}
          />
        </div>
        <div className='right'>
          {/* <div style={{marginRight: 20, marginTop: 0}}>
            <ChatIcon fontSize='small' style={{color: '#a3a6b3'}}/>
          </div> */}
          <Lock className='lockIcon' />
          <span className='personalView'>Personal View</span>
          {query.get('view')?.toLowerCase() !== practitionerViewConfig.toLowerCase() && (
            <div className='viewSettings'>
              <svg width='1' height='10' viewBox='0 0 1 10' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <line x1='0.25' y1='-1.09278e-08' x2='0.25' y2='10' stroke='#A3A6B3' strokeWidth='0.5' />
              </svg>
              <svg
                width='12'
                height='12'
                viewBox='0 0 12 12'
                style={{ marginLeft: 8 }}
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M8.38548 0C8.75792 0 9.11509 0.147948 9.37844 0.411297L11.5887 2.62156C11.8521 2.88491 12 3.24208 12 3.61452V10.5957C12 11.3713 11.3713 12 10.5957 12H1.40426C0.628707 12 0 11.3713 0 10.5957V1.40426C0 0.628707 0.628707 0 1.40426 0H8.38548ZM8.38548 0.765957L7.91489 0.765702V2.17021C7.91489 2.80475 7.4005 3.31915 6.76596 3.31915H2.80851C2.17397 3.31915 1.65957 2.80475 1.65957 2.17021V0.765702L1.40426 0.765957C1.05173 0.765957 0.765957 1.05173 0.765957 1.40426V10.5957C0.765957 10.9483 1.05173 11.234 1.40426 11.234H10.5957C10.9483 11.234 11.234 10.9483 11.234 10.5957V3.61452C11.234 3.44523 11.1668 3.28288 11.0471 3.16317L8.83683 0.952911C8.71712 0.833206 8.55477 0.765957 8.38548 0.765957ZM6 4.85106C7.33958 4.85106 8.42553 5.93701 8.42553 7.2766C8.42553 8.61618 7.33958 9.70213 6 9.70213C4.66042 9.70213 3.57447 8.61618 3.57447 7.2766C3.57447 5.93701 4.66042 4.85106 6 4.85106ZM6 5.61702C5.08344 5.61702 4.34043 6.36004 4.34043 7.2766C4.34043 8.19315 5.08344 8.93617 6 8.93617C6.91656 8.93617 7.65957 8.19315 7.65957 7.2766C7.65957 6.36004 6.91656 5.61702 6 5.61702ZM7.14894 0.765702H2.42553V2.17021C2.42553 2.38173 2.597 2.55319 2.80851 2.55319H6.76596C6.97747 2.55319 7.14894 2.38173 7.14894 2.17021V0.765702Z'
                  fill='#A3A6B3'
                />
              </svg>
              <span className='changedText'>
                You changed the layout.
                <span
                  onClick={() => updatePersonalView()}
                  onKeyPress={event => event.key === 'Enter' && updatePersonalView()}
                  tabIndex={0}
                  role='button'
                >
                  Save
                </span>
                or
                <span
                  onClick={() => window.location.reload()}
                  onKeyPress={event => event.key === 'Enter' && window.location.reload()}
                  tabIndex={0}
                  role='button'
                >
                  Reload
                </span>
              </span>
            </div>
          )}
          <Link className='link' to={{ pathname: '/home', search: '?view=card' }}>
            <CardView
              className='cardView'
              style={{
                color: query.get('view') === 'card' ? '#417EB9' : '#A3A6B3',
              }}
            />
          </Link>
          <Link className='link' to={{ pathname: '/home', search: '?view=table' }}>
            <Grid
              style={{
                cursor: 'pointer',
                color: query.get('view') === 'table' ? '#417EB9' : '#A3A6B3',
              }}
            />
          </Link>
        </div>
      </div>
      {query.get('view') === 'card' && (
        <CardsView
          loadingCount={isLoadingPatients ? 1 : 0}
          patients={patients}
          filterValue={filterValue}
          criticalCollapsed={criticalCollapsed}
          setCriticalCollapsed={setCriticalCollapsed}
          history={history}
          setSelectedPatient={setSelectedPatientAction}
          getPatientsAction={() => Promise.resolve()}
          atRiskCollapsed={atRiskCollapsed}
          setAtRiskCollapsed={setAtRiskCollapsed}
          stableCollapsed={stableCollapsed}
          setStableCollapsed={setStableCollapsed}
          setNote={setLastNote}
          practitionerFirebaseId={practitioner?.firebaseUid}
        />
      )}
      {query.get('view') === 'table' && (
        <div className='tableContainer'>
          {patients?.length > 0 && (
            <TableView
              isLoading={isLoadingPatients || !patients.every(p => p.state === 'LISTENING')}
              history={history}
              patients={patients}
              filterValue={filterValue}
              setSelectedPatientAction={setSelectedPatientAction}
              setNote={setLastNote}
            />
          )}
        </div>
      )}

      {/* NO CLIENTS FOUND TODO: FIX */}
      {patients?.length === 0 && !isLoadingPatients && (
        <div className='noClients'>
          <svg width='120' height='120' viewBox='0 0 120 120' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <path
              d='M112.449 59.9703C112.449 61.376 112.399 62.7817 112.299 64.1874C112.248 64.8902 112.173 65.568 112.098 66.2708C112.048 66.6222 112.023 66.9485 111.972 67.2999C112.098 66.4465 111.872 67.8522 111.847 68.0028C111.421 70.6886 110.793 73.3494 109.965 75.9348C109.589 77.1146 109.188 78.2693 108.736 79.4239C108.611 79.7252 108.485 80.0515 108.36 80.3527C107.959 81.3819 108.686 79.675 108.209 80.679C107.883 81.3819 107.582 82.0847 107.231 82.7875C106.077 85.1722 104.748 87.4564 103.242 89.6402C102.866 90.1925 102.49 90.7196 102.113 91.2467C101.913 91.5228 101.712 91.7738 101.537 92.05C101.461 92.1504 101.085 92.6022 101.511 92.0751C100.633 93.1795 99.7052 94.284 98.7519 95.3383C96.9708 97.2711 95.0643 99.0784 93.0323 100.76C92.7814 100.961 92.5305 101.162 92.2546 101.388C91.4017 102.065 92.8567 100.961 91.9787 101.614C91.4518 102.015 90.925 102.392 90.3731 102.768C89.2944 103.521 88.1906 104.249 87.0617 104.927C85.9329 105.605 84.7789 106.257 83.5998 106.86C83.0479 107.136 82.5211 107.412 81.9692 107.663C81.7184 107.789 81.4675 107.889 81.2167 108.014C81.0912 108.065 79.8118 108.617 80.5895 108.291C78.106 109.345 75.5472 110.173 72.9633 110.851C71.6337 111.177 70.3041 111.478 68.9745 111.729C68.2972 111.855 67.6199 111.955 66.9426 112.056C67.7955 111.93 66.6666 112.081 66.5663 112.106C66.2151 112.156 65.8889 112.181 65.5377 112.206C62.6277 112.508 59.6926 112.558 56.7826 112.382C53.5716 112.206 52.3173 112.031 49.2317 111.378C46.5976 110.826 43.9886 110.073 41.4549 109.119C40.878 108.918 40.301 108.692 39.7491 108.441C39.5735 108.366 38.8961 108.065 39.5735 108.366C39.2724 108.24 38.9714 108.09 38.6704 107.964C37.3659 107.362 36.0865 106.734 34.8071 106.031C32.4991 104.751 30.2665 103.321 28.1592 101.739C28.084 101.689 27.231 101.011 27.8833 101.538C27.6324 101.338 27.3565 101.137 27.1056 100.911C26.629 100.534 26.1774 100.133 25.7259 99.7561C24.6973 98.8525 23.6939 97.9237 22.7406 96.9447C21.8124 96.016 20.9093 95.037 20.0564 94.0581C19.7052 93.6314 19.3539 93.2297 19.0027 92.803C18.802 92.552 18.6014 92.301 18.3756 92.0249C18.8773 92.6775 18.0495 91.5981 17.9742 91.4726C16.3938 89.339 14.9889 87.1301 13.7346 84.7957C13.1075 83.6159 12.5054 82.4361 11.9535 81.2313C11.8281 80.93 11.6775 80.6288 11.5521 80.3276C11.8782 81.1057 11.4518 80.0766 11.4267 80.0013C11.1758 79.3737 10.925 78.7462 10.6992 78.1187C9.77099 75.5834 9.04349 72.9729 8.49159 70.3121C8.36616 69.6344 8.24073 68.9818 8.1153 68.304C8.06513 67.9777 8.01495 67.6263 7.96478 67.2748C7.78918 66.1453 8.04004 68.053 7.91461 66.8983C7.739 65.4675 7.61357 64.0117 7.53831 62.5558C7.41288 59.6942 7.48814 56.8075 7.78918 53.9711C7.81426 53.695 7.83935 53.3937 7.88952 53.1176C7.91461 52.9168 8.01495 52.1638 7.91461 52.9168C8.01495 52.2391 8.1153 51.5613 8.24073 50.8836C8.49159 49.4277 8.81771 47.9969 9.19401 46.5662C9.52013 45.3613 9.87134 44.1815 10.2476 43.0018C10.4483 42.3742 10.6741 41.7467 10.925 41.1191C11.0504 40.8179 11.1507 40.4916 11.2762 40.1904C11.3263 40.0649 11.8782 38.7847 11.527 39.5378C12.5807 37.1029 13.8099 34.7434 15.1896 32.4842C15.8168 31.4551 16.4941 30.451 17.1714 29.447C17.5477 28.9198 17.9491 28.3676 18.3254 27.8405C18.9776 26.9619 17.8488 28.4178 18.5512 27.5644C18.8271 27.2129 19.1031 26.8866 19.379 26.5352C21.0598 24.5271 22.8911 22.6194 24.8227 20.8623C25.8011 19.9837 26.8046 19.1303 27.8331 18.327C27.1809 18.8542 28.0338 18.1764 28.109 18.1262C28.2847 18.0007 28.4603 17.8752 28.6359 17.7246C29.263 17.2728 29.8902 16.821 30.5173 16.3942C32.7249 14.9132 35.0078 13.608 37.391 12.4533C37.993 12.1521 38.5951 11.9011 39.1972 11.625C40.2006 11.1731 38.4948 11.9011 39.5233 11.4743C39.8243 11.3488 40.1505 11.2233 40.4515 11.0978C41.7058 10.6209 42.9601 10.1691 44.2395 9.76745C46.8987 8.9391 49.608 8.33667 52.3424 7.90994C52.518 7.88484 53.2957 7.78443 52.5431 7.88484C52.8943 7.83464 53.2204 7.80954 53.5716 7.75933C54.274 7.68403 54.9513 7.60872 55.6538 7.55852C57.184 7.43301 58.7394 7.38281 60.2696 7.40791C63.6814 7.43301 66.4158 7.68403 69.401 8.26136C72.5117 8.8638 75.5973 9.71725 78.5575 10.8217C80.0878 11.399 80.4892 11.5748 81.9943 12.2776C83.5246 13.0055 85.0047 13.7837 86.4597 14.6371C87.6889 15.3651 88.868 16.1181 90.047 16.9465C91.5773 18.0258 92.1543 18.4525 93.5591 19.6574C103.067 27.7401 109.238 38.3329 111.521 50.6577C112.173 53.6197 112.424 56.6067 112.449 59.9703C112.449 61.9282 114.18 63.8359 116.212 63.7355C118.244 63.6351 119.975 62.0788 119.975 59.9703C119.925 45.788 114.907 32.1579 105.826 21.289C97.1715 10.9221 84.804 3.59249 71.5334 1.08235C58.8648 -1.32739 45.8199 0.304205 34.1548 5.80142C23.744 10.6962 14.8635 18.804 8.8428 28.5935C2.62141 38.7094 -0.489288 50.733 0.0626101 62.606C0.614508 74.3786 4.57814 85.9754 11.6274 95.4387C18.7268 105.002 28.3348 112.257 39.5233 116.398C50.787 120.565 63.4054 121.092 75.0203 118.105C86.2088 115.219 96.5694 108.968 104.346 100.409C112.173 91.7739 117.516 80.8798 119.247 69.3332C119.699 66.2206 119.975 63.1331 120 59.9954C120 58.0375 118.269 56.1298 116.237 56.2302C114.155 56.2804 112.449 57.8618 112.449 59.9703Z'
              fill='#A3A6B3'
            />
            <path
              d='M97.899 18.3779C95.265 21.0135 92.656 23.6241 90.0219 26.2597C83.6751 32.6104 77.3534 38.936 71.0066 45.2866C63.3553 52.9426 55.729 60.5734 48.0777 68.2294C41.48 74.831 34.8573 81.4578 28.2596 88.0595C25.0485 91.2725 21.7121 94.4102 18.5763 97.7486C18.5261 97.7989 18.4759 97.8491 18.4509 97.8742C17.0209 99.3049 17.0209 101.765 18.4509 103.196C19.8808 104.626 22.3392 104.626 23.7691 103.196C26.4032 100.56 29.0122 97.9495 31.6462 95.3138C37.993 88.9631 44.3148 82.6376 50.6616 76.2869C58.3129 68.631 65.9391 61.0001 73.5905 53.3442C80.1881 46.7425 86.8109 40.1157 93.4086 33.5141C96.6196 30.3011 99.9561 27.1634 103.092 23.8249C103.142 23.7747 103.192 23.7245 103.217 23.6994C104.647 22.2686 104.647 19.8087 103.217 18.3779C101.787 16.922 99.354 16.922 97.899 18.3779Z'
              fill='#A3A6B3'
            />
          </svg>
          <span className='title'>You have no clients</span>
          <span>Get started by adding your first client</span>
          <button className='addButton' onClick={() => setShowUpsertClientModal(true)}>
            Add New Client
          </button>
        </div>
      )}
      <InsightDetailsModal
        insight={selectedPatient?.insight}
        hide={() => setSelectedPatientAction({ selectedPatient: undefined, selectedAction: '' })}
        show={!!selectedPatient?.insight && selectedAction === 'Insight'}
        practitioner={notAsked()}
        patient={selectedPatient}
        getPatientStatusElement={getPatientStatusElement}
        clearAlertAction={clearAlertAction}
      />
      {showUpsertClientModal && (
        <UpsertClientContainer
          setShow={setShowUpsertClientModal}
          editMode={false}
          organization={practitioner?.organization}
          selectedPatient={notAsked()}
          selectedPatientAllies={notAsked()}
          refetchPatient={() => {}}
          setShowDischargeClientModal={setShowDischargeClientModal}
        />
      )}
      {selectedAction === 'Notes' && (
        <NoteModal
          onClose={() => setSelectedPatientAction({ selectedPatient: undefined, selectedAction: '' })}
          note={lastNote.status === 'Done' ? lastNote.data.values[0] : {}}
          patient={selectedPatient}
        />
      )}
    </div>
  );
};
